/**
 * auth.js
 * 认证相关工具函数
 * 提供用户认证、会话管理等功能
 */

// 存储在本地的键名常量
const SESSION_MARKER = 'auth-session-marker';  // 会话标记键，用于跨标签页session同步
const AUTH_TOKEN = 'auth-token';               // 登录令牌键
const USER_INFO = 'user-info';                 // 用户信息键
const SYS_LIST = 'sys-list';                   // 系统列表键
const CURRENT_SYSTEMD = 'current-sys-index';   // 当前选中的系统索引键

/**
 * 生成唯一会话ID
 * 使用时间戳和随机数组合生成唯一标识
 * @returns {string} 会话ID
 */
const generateSessionId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 检查用户是否已登录
 * @returns {boolean} 登录状态
 */
export const isLogin = () => {
    return getUserInfo().isLogin;
}

/**
 * 获取系统列表
 * @returns {Array} 系统列表数据
 */
export const getSystemList = () => {
    return JSON.parse(sessionStorage.getItem(SYS_LIST));
}

/**
 * 获取当前选中的系统索引
 * @returns {string} 系统索引
 */
export const getCurrentSystem = () => {
    return JSON.parse(sessionStorage.getItem(CURRENT_SYSTEMD));
}

/**
 * 保存系统列表到会话存储
 * @param {Array} systemList 系统列表数据
 */
export const setSystemList = (systemList) => {
    sessionStorage.setItem(SYS_LIST, JSON.stringify(systemList));
}

/**
 * 检查当前系统是否与本地系统一致
 * @param {number} tenantSystemId 系统索引
 * @returns {boolean} 是否一致
 */
export const isCurrentSystem = () => {
   const sessionCurrent =  JSON.parse(sessionStorage.getItem(CURRENT_SYSTEMD));
   const localCurrent = JSON.parse(localStorage.getItem(CURRENT_SYSTEMD));
   if(sessionCurrent != null && localCurrent != null){
    return sessionCurrent.staffId == localCurrent.staffId 
    && sessionCurrent.tenantSystemId == localCurrent.tenantSystemId;
   }
   if(sessionCurrent == null || localCurrent == null){
    return false
   }
   return false
}

/**
 * 设置当前选中的系统索引
 * @param {number} tenantSystemId 系统索引
 */
export const setCurrentSystem = (params) => {
    sessionStorage.setItem(CURRENT_SYSTEMD, JSON.stringify(params));
    localStorage.setItem(CURRENT_SYSTEMD, JSON.stringify(params));
}

/**
 * 设置认证令牌
 * @param {string} token 认证令牌
 */
export const setToken = (token) => {
    // 存储登录态到LocalStorage
    sessionStorage.setItem("accessToken",token);
    localStorage.setItem(AUTH_TOKEN, token);
};

/**
 * 设置用户认证信息
 * 包括token和用户信息的存储，同时处理会话标记
 * @param {string} token 认证令牌
 * @param {Object} userInfo 用户信息对象
 */
export const setAuth = (token, userInfo) => {
    // 存储登录态到LocalStorage
    localStorage.setItem(AUTH_TOKEN, token);
    localStorage.setItem(USER_INFO, JSON.stringify(userInfo));

    // 生成并存储会话标记，用于跨标签页同步
    const sessionId = generateSessionId();
    localStorage.setItem(SESSION_MARKER, sessionId);
    sessionStorage.setItem(SESSION_MARKER, sessionId);

    // 触发LocalStorage更新事件，用于通知其他标签页
    localStorage.setItem('auth-update', Date.now());
};

/**
 * 获取认证令牌
 * @returns {string} 认证令牌
 */
export const getToken = () => {
    return localStorage.getItem(AUTH_TOKEN);
};

/**
 * 清除所有认证信息
 * 用于用户登出时清理数据
 */
export const clearAuth = () => {
    localStorage.clear();
    sessionStorage.clear();
    // 通知其他标签页更新状态
    localStorage.setItem('auth-update', Date.now());
};

/**
 * 获取用户信息和登录状态
 * 通过比对会话标记确保登录态有效
 * @returns {Object} 包含登录状态和用户信息的对象
 */
export const getUserInfo = () => {
    const localSessionId = localStorage.getItem(SESSION_MARKER);
    const sessionSessionId = sessionStorage.getItem(SESSION_MARKER);

    // 会话标记不匹配时，视为登录态失效
    if (localSessionId == null || sessionSessionId == null || localSessionId !== sessionSessionId) {
        return {
            isLogin: false,
            token: null,
            userInfo: null,
            localSessionId: localSessionId,
            sessionSessionId: sessionSessionId
        };
    }

    return {
        isLogin: true,
        localSessionId: localSessionId,
        sessionSessionId: sessionSessionId,
        token: localStorage.getItem(AUTH_TOKEN),
        userInfo: localStorage.getItem(USER_INFO)
            ? JSON.parse(localStorage.getItem(USER_INFO))
            : null
    };
};

/**
 * 检查并更新登录状态
 * 用于新标签页初始化时同步登录状态
 * @param {Object} store Vuex store实例
 */
export const checkAuthStatus = (store) => {
    // 1. 首先检查 localStorage 中是否存在会话标记
    const localSessionId = localStorage.getItem(SESSION_MARKER);

    if (localSessionId) {
        // 2. 如果存在，同步到当前标签页的 sessionStorage
        sessionStorage.setItem(SESSION_MARKER, localSessionId);

        // 3. 获取并检查认证信息
        const authInfo = getUserInfo();
        if (authInfo.isLogin) {
            // 4. 如果已登录，更新 store
            store.commit('setAuth', authInfo);
            return true;
        }
    }

    // 5. 如果未登录或无会话标记，清除认证状态
    store.commit('clearAuth');
    return false;
};

/**
 * 注册跨标签页监听器
 * 用于同步不同标签页间的登录状态
 * @param {Object} store Vuex store实例
 */
export const setupCrossTabListener = (store) => {
    // 1. 初始检查
    checkAuthStatus(store);

    // 2. 监听 storage 事件
    window.addEventListener('storage', (event) => {
        console.log('Storage event:', event);

        // 只处理认证相关的变更
        if (event.key === AUTH_TOKEN ||
            event.key === SESSION_MARKER ||
            event.key === 'auth-update') {

            // 重新检查认证状态
            checkAuthStatus(store);
        }

        if (event.key === CURRENT_SYSTEMD) {
            // 其他页签切换了系统
            window.location.href = '/'; 
        }
    });

    // 3. 定期检查会话有效性（可选，防止会话状态不同步）
    setInterval(() => {
        const authInfo = getUserInfo();
        if (!authInfo.isLogin) {
            store.commit('clearAuth');
        }
    }, 30000); // 每30秒检查一次
};